import { useState } from 'react';

import { useUnidadesAdminControllerExtrato } from '@/api/generated/admin-unidades/admin-unidades';
import type { SaldoDeAmbienteResponse } from '@/api/generated/model';
import { EstadoCarregando, EstadoErro, EstadoVazio } from '@/shared/components/estados';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import { Paginacao } from '@/shared/ui/pagination';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/shared/ui/table';

const ROTULO_TIPO: Record<string, string> = {
  CREDITO: 'Crédito',
  CONSUMO: 'Consumo',
  ESTORNO: 'Estorno',
  AJUSTE: 'Ajuste',
};

interface Props {
  aberto: boolean;
  aoFechar: () => void;
  ambiente: SaldoDeAmbienteResponse;
}

/**
 * Extrato do ambiente.
 *
 * ⚠️ Só leitura, e é o ponto: o ledger é append-only. Não há editar nem
 * excluir lançamento em lugar nenhum da interface porque não há no servidor —
 * corrigir é lançar um ajuste, que fica no extrato ao lado do erro que corrigiu.
 */
export function ExtratoDialogo({ aberto, aoFechar, ambiente }: Props) {
  const [pagina, setPagina] = useState(1);

  const { data, isPending, isError, refetch } = useUnidadesAdminControllerExtrato(
    ambiente.tenantId,
    { pagina, porPagina: 10, ordenarPor: 'criadoEm', ordem: 'desc' },
    { query: { enabled: aberto } },
  );

  return (
    <Dialog open={aberto} onOpenChange={(v) => !v && aoFechar()}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Extrato — {ambiente.tenantNome}</DialogTitle>
          <DialogDescription>
            Todo lançamento que compõe o saldo. Nenhum deles pode ser editado ou apagado.
          </DialogDescription>
        </DialogHeader>

        {isPending ? (
          <EstadoCarregando rotulo="Carregando extrato..." />
        ) : isError || !data ? (
          <EstadoErro aoTentarDeNovo={() => void refetch()} />
        ) : (
          <div className="space-y-4">
            <div className="rounded-lg bg-muted/50 px-4 py-3 flex justify-between text-sm">
              <span className="text-muted-foreground">Saldo atual</span>
              <span className="text-brand font-semibold text-base">{data.ambiente.saldo}</span>
            </div>

            {data.itens.length === 0 ? (
              <EstadoVazio
                titulo="Nenhum lançamento"
                descricao="Este ambiente nunca movimentou unidades."
              />
            ) : (
              <>
                <div className="overflow-x-auto max-h-80">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Quando</TableHead>
                        <TableHead>Tipo</TableHead>
                        <TableHead className="text-right">Quantidade</TableHead>
                        <TableHead className="text-right">Saldo</TableHead>
                        <TableHead>Motivo</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {data.itens.map((transacao) => (
                        <TableRow key={transacao.id}>
                          <TableCell className="whitespace-nowrap text-muted-foreground">
                            {new Date(transacao.criadoEm).toLocaleDateString('pt-BR')}
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline">
                              {ROTULO_TIPO[transacao.tipo] ?? transacao.tipo}
                            </Badge>
                          </TableCell>
                          <TableCell
                            className={`text-right font-medium ${
                              transacao.quantidade < 0 ? 'text-destructive' : 'text-emerald-600'
                            }`}
                          >
                            {transacao.quantidade > 0 ? '+' : ''}
                            {transacao.quantidade}
                          </TableCell>
                          <TableCell className="text-right text-muted-foreground">
                            {transacao.saldoApos}
                          </TableCell>
                          <TableCell>
                            <span className="text-foreground">{transacao.descricao ?? '—'}</span>
                            {transacao.criadoPorNome && (
                              <p className="text-xs text-muted-foreground mt-0.5">
                                por {transacao.criadoPorNome}
                              </p>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>

                <Paginacao
                  pagina={data.pagina}
                  totalPaginas={data.totalPaginas}
                  total={data.total}
                  porPagina={data.porPagina}
                  aoMudarPagina={setPagina}
                />
              </>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={aoFechar}>
            Fechar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
